// The Circuit Breaker: a switch in a conductor, and the one thing in the Charge
// system that makes overshoot cost something.
//
// **The mechanism is one line, and it is not in this file.** An open breaker
// fails `queries.isConductive`, exactly as a rusted one does, and everything
// else follows with no further rule: the network walk stops at it, a
// `conduit.feed.pathTiles` path reads broken, a cable draws its run as ending
// there, every reader beyond it goes dark, and a live floor grid on the far side
// stops being live. What is in this file is only the two ways the switch gets
// *set*, and what it looks like once it is.
//
// **Why it latches.** Opening a breaker splits its network in two, which drops
// the very potential that opened it -- so a live "open exactly while overloaded"
// reading would close it again on the same settle and oscillate forever. It has
// to remember, which makes `breaker.open` real persisted state and a Circuit
// Breaker a `Remembered` object (see `objects/objectDetail.ts`), with `gate.open`
// as its nearest relative. A real breaker latches for the same reason, which is
// a pleasant thing to find at the end of the argument rather than the start.
//
// **Why the trip reads a potential rather than a current.** This simulation has
// no current: charge redistributes instantly and totally (see `chargeBind.ts`),
// so there is no rate of flow anywhere to compare against a rating. Potential is
// what the model has and what every other Charge reader already asks for, so
// this asks the same question -- which makes the breaker an over-*voltage*
// device. That is a real thing rather than a compromise, and it is the reading
// the player can actually count: a Charged Font holds its line at 8, one
// ordinary Standard cast of Charge on top of it makes 12, and the shipped rating
// is 12.
//
// One thing worth knowing before authoring with it: **more sources never trip
// it.** Each Font brings a unit of capacity along with its 8, so N of them still
// read 8. What trips a breaker is charge poured onto a line that has nowhere to
// put it -- overshoot, not scale.
import { ObjectDef, RoomDef } from './types';
import { PhysicsConfig } from './physics';
import { SimEvents } from './events';
import { WorldState } from './state';
import { applyStagePresentation } from './presentation';
import { BREAKER, rustStage } from './rust';
import { networkPotential } from './queries';

/** What a breaker in the **open** position is called and reads as, in each
 * corrosion stage. The closed rows live in `rust.ts` with every other rust
 * family (`BREAKER`), so that `rustFamily` stays one dispatch and the
 * Encyclopedia's ladder finds something true; these cross them with the switch
 * position, which is the half `rust.ts` cannot know.
 *
 * The texture keys are the closed ones plus `-open`, so the four are one family
 * rather than four unrelated sprites -- the same object with its blade up. */
const OPEN: Record<'clean' | 'rusted', { name: string; texture: string; description: string }> = {
  clean: {
    name: 'Open Circuit Breaker',
    texture: 'object-breaker-open',
    description: 'The blade stands up out of its housing, and there is a finger of air where the line used to be. Nothing is crossing this.',
  },
  rusted: {
    name: 'Open Corroded Circuit Breaker',
    texture: 'object-breaker-rusted-open',
    description: 'Rust-scaled contacts, and the blade standing open above them. Two reasons nothing is getting through, where one would have done.',
  },
};

/** How this breaker currently looks and reads -- its corrosion stage crossed
 * with its switch position. */
function breakerStage(obj: ObjectDef, physics: PhysicsConfig): { name: string; texture: string; description: string } {
  const stage = rustStage(obj, physics);
  return obj.breaker?.open ? OPEN[stage] : BREAKER[stage];
}

/** Throws one breaker, by hand, and hands back the position it landed in.
 *
 * Called from `sim/interact.ts`, which is the only way a player reaches it:
 * carrying the cluster is the whole signal that Interact throws it, with no
 * `interact` field saying so -- the same way carrying `vines` is the whole
 * signal an object follows the vine ladder. Closing one that tripped on an
 * overload which is still there simply trips it again on the next settle, which
 * is exactly what a real one does and needs no rule of its own. */
export function throwBreaker(obj: ObjectDef): boolean {
  const breaker = obj.breaker;
  if (!breaker) return false;
  breaker.open = !breaker.open;
  return breaker.open;
}

/** The declared system's own body (see settle.ts): trips every breaker whose
 * own network has gone over its rating, and repaints all of them.
 *
 * **The loop is not defensiveness.** Opening a breaker splits a network, and
 * removing a store from a segment raises what the rest of that segment reads
 * (`SumC` falls while `SumQ` does not) -- so one breaker going can genuinely put
 * another over its own rating, in the same settle. A single pass would leave
 * that second one closed until the next mutation happened to re-settle, which is
 * a circuit that is wrong on screen for as long as the player does not touch
 * anything. It terminates because tripping is monotonic: a breaker only ever
 * opens here, so each round either changes nothing and stops, or removes one
 * from the pool of closed ones.
 *
 * This is a *local* fixpoint inside one system, which is not the thing
 * `settle.ts` refuses to run: that rule is about the system graph, where a
 * while-loop would hide a cycle between systems rather than resolve a cascade
 * inside one. */
export function settleBreakers(world: WorldState, out: SimEvents): void {
  const room: RoomDef | undefined = world.rooms[world.player.roomId];
  if (!room) return;
  const breakers = room.objects.filter((o) => o.breaker);
  if (breakers.length === 0) return;

  for (let round = 0; round < breakers.length; round++) {
    let tripped = false;
    for (const obj of breakers) {
      const rating = obj.breaker!.trips;
      if (rating === undefined || obj.breaker!.open) continue;
      // No caster passed, deliberately, and for the same reason the editor's
      // hazard overlay withholds one: what a breaker is rated against is what
      // the *line* carries, not what changes about it while somebody happens to
      // be wired into it. A caster who joins a network with `Bind`·`Charge` adds
      // a unit of capacity and lowers its potential, and a breaker that stopped
      // reading over-rating because of that would be measuring the observer.
      if (networkPotential(room, obj, world.physics) < rating) continue;
      obj.breaker!.open = true;
      tripped = true;
    }
    if (!tripped) break;
  }

  for (const obj of breakers) applyStagePresentation(obj, breakerStage(obj, world.physics), out);
}
